// eslint-disable-next-line @nx/enforce-module-boundaries
import { TamaroConfig } from '@api/raisenow';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { getConnectedTamaroPaymentMethod, usesTamaro } from '@fmnts/api';
import { CampaignForm } from '@formunauts/shared/campaigning/domain';
import { Brand, pipe } from 'effect';
import * as O from 'effect/Option';
import * as S from 'effect/String';
/**
 * Hash for the self-checkout donation
 */
export type Hash = string & Brand.Brand<'hash'>;
export const Hash = Brand.nominal<Hash>();

/**
 * Self-checkout forms, describes the format for both:
 * - authenticating step (birthday form)
 * - self-checkout form with payment-methods and additional fields
 */
interface BaseForm {
  readonly organizationLogo: string;
  readonly language: string;
  readonly locale: string;
  readonly form: CampaignForm.Page;
  readonly style: string;
}

export type AuthenticationForm = BaseForm & Brand.Brand<'authenticationForm'>;
export const AuthenticationForm = Brand.nominal<AuthenticationForm>();

export type SelfCheckoutForm = BaseForm & Brand.Brand<'selfCheckoutForm'>;
export const SelfCheckoutForm = Brand.nominal<SelfCheckoutForm>();

export type UnsubscribeForm = BaseForm & Brand.Brand<'unsubscribeForm'>;
export const UnsubscribeForm = Brand.nominal<UnsubscribeForm>();

/**
 * Interface for the data that is prepared using `prepareFormData`.
 */
export interface PreparedData {
  readonly [k: string]: unknown;
}

export type AuthenticationData = PreparedData &
  Brand.Brand<'authenticationData'>;
export const AuthenticationData = Brand.nominal<AuthenticationData>();
export type SelfCheckoutData = PreparedData & Brand.Brand<'selfCheckoutData'>;
export const SelfCheckoutData = Brand.nominal<SelfCheckoutData>();

export type UnsubscribeCheckoutData = PreparedData &
  Brand.Brand<'unsubscribeCheckoutData'>;
export const UnsubscribeCheckoutData = Brand.nominal<UnsubscribeCheckoutData>();

/**
 * The device of the donor, receiving the self-checkout link
 *
 * This data is sent along with the form @see {@link BaseForm}
 */
export interface DonorDevice {
  languages: string[];
  locale: string;
}

export enum FlowStatus {
  Init = 'init',
  InProgress = 'in_progress',
  Error = 'error',
  Done = 'done',
}

export enum DonationStatus {
  Finished = 'finished',
  Pending = 'pending',
  Unfinished = 'unfinished',
}
export interface TamaroInstance {
  readonly instance: string;
  readonly config: Readonly<TamaroConfig>;
}

export type SelfCheckoutPaymentStep = 'tamaro';
/**
 * The response for uploading self-checkout form-values
 *
 * The endpoint will either respond with a Tamaro-config
 * or return with 204, indicating that self-checkout is complete
 */
export interface DonationResponse {
  readonly tamaro?: TamaroInstance;
  readonly status: DonationStatus;
}

export type SelfCheckoutResponseTamaro = WithRequired<
  DonationResponse,
  'tamaro'
>;
export type SelfCheckoutResponseDefault = WithRequired<
  DonationResponse,
  'status'
>;

export type SelfCheckoutResponse = DonationResponse &
  Brand.Brand<'checkoutResponse'>;
export const SelfCheckoutResponse = Brand.nominal<SelfCheckoutResponse>();

export interface PreparedSelfCheckoutFormPayload {
  /** prepared payload */
  readonly payload: SelfCheckoutData;
  /** all form data */
  readonly formData: Record<string, unknown>;
}

export interface PreparedUnsubscribeFormPayload {
  readonly hash: Hash;
  readonly formData: UnsubscribeCheckoutData;
}

/**
 * @param data
 * @returns
 * The required online payment step or `none`.
 */
export function requiresPaymentStep(
  data: PreparedSelfCheckoutFormPayload,
): O.Option<SelfCheckoutPaymentStep> {
  const paymentMethod = getPaymentMethod(data);

  if (O.isSome(paymentMethod) && usesTamaro(paymentMethod.value)) {
    return O.some('tamaro');
  }

  return O.none();
}

function getPaymentMethod(
  data: PreparedSelfCheckoutFormPayload,
): O.Option<string> {
  // First, try to get from payload
  let paymentMethod: unknown = data.payload['payment_method'];
  if (S.isString(paymentMethod)) {
    return O.some(paymentMethod);
  }

  // Then, try to get from form data
  paymentMethod = data.formData['payment_method'];
  if (S.isString(paymentMethod)) {
    return O.some(paymentMethod);
  }

  return O.none();
}

export function statusAfterCheckoutUpload(
  data: PreparedSelfCheckoutFormPayload,
): DonationStatus {
  const paymentStep = O.getOrNull(requiresPaymentStep(data));
  switch (paymentStep) {
    case 'tamaro':
      return DonationStatus.Pending;
    default:
      return DonationStatus.Finished;
  }
}

export function getTamaroPaymentMethodsForSelfCheckout(
  data: PreparedSelfCheckoutFormPayload,
): O.Option<ReadonlyArray<string>> {
  return pipe(
    getPaymentMethod(data),
    O.flatMap((p) =>
      usesTamaro(p) ? O.some(getConnectedTamaroPaymentMethod(p)) : O.none(),
    ),
  );
}

export function isPendingUploadResult(
  result: SelfCheckoutResponse,
): result is SelfCheckoutResponse {
  return !!result && result.status === DonationStatus.Pending;
}
/**
 * Errors
 */
import { HttpErrorResponse } from '@angular/common/http';
import { ResultStream } from '@causality/core';
import * as Data from 'effect/Data';

export class SelfCheckoutCompletedError extends Data.TaggedError(
  'SelfCheckoutCompletedError',
) {}

export class SelfCheckoutCSRFError extends Data.TaggedError(
  'SelfCheckoutCSRFError',
)<{
  httpError: HttpErrorResponse;
}> {}
export class SelfCheckoutCustomError extends Data.TaggedError(
  'SelfCheckoutCustomError',
)<{
  status?: number;
  message?: string;
}> {}

export type SelfCheckoutError =
  | ResultStream.ClientOfflineError
  | ResultStream.ClientTimeoutError
  | SelfCheckoutCompletedError
  | SelfCheckoutCustomError
  | SelfCheckoutCSRFError;

type WithRequired<T, K extends keyof T> = T & Required<Pick<T, K>>;
